/**
 * Ciclo de ajuste de un Registro de Recursos ya reportado.
 *
 * # Por qué existe
 *
 * El día en curso se digita libremente. Un día YA reportado es distinto: sus
 * recursos ya bajaron a los reportes diarios, que pueden estar aprobados y
 * firmados. Corregirlo exige dejar constancia de quién pidió el cambio, quién
 * lo autorizó y quién lo aprobó.
 *
 * ```
 *   (contratista)          (PL2)              (contratista)
 *   Solicitar ajuste  →  Autorizar  →  Editar y guardar
 *                            ↓                    ↓
 *                        Rechazar          (INT, opcional)
 *                                          Dar concepto
 *                                                 ↓
 *                                             (PL2)
 *                                          Aprobar el cambio
 *                                                 ↓
 *                                     se propaga a los reportes
 * ```
 *
 * El concepto del INT es OPCIONAL: el PL2 puede aprobar sin él. Exigirlo
 * dejaría bloqueada la corrección en las OT que no tienen interventor
 * asignado.
 */

import type { AjusteRr, EstadoAjusteRr } from "@app-types/entities";

/** Transiciones válidas. Fuera de aquí, no hay camino. */
export const TRANSICIONES_AJUSTE_RR: Record<EstadoAjusteRr, EstadoAjusteRr[]> = {
  SOLICITADO: ["AUTORIZADO", "RECHAZADO"],
  AUTORIZADO: ["EN_REVISION", "RECHAZADO"],
  EN_REVISION: ["APROBADO", "RECHAZADO", "AUTORIZADO"],
  // Terminales: un ajuste resuelto no se reabre. Para otra corrección se pide
  // una solicitud nueva, y así cada cambio conserva su propia traza.
  APROBADO: [],
  RECHAZADO: [],
};

export function puedeTransicionarAjuste(
  actual: EstadoAjusteRr,
  destino: EstadoAjusteRr,
): boolean {
  return (TRANSICIONES_AJUSTE_RR[actual] ?? []).includes(destino);
}

/** ¿Hay un ajuste en curso, sin resolver? */
export function ajusteEnCurso(ajuste: AjusteRr | undefined): boolean {
  if (!ajuste) return false;
  return ajuste.estado !== "APROBADO" && ajuste.estado !== "RECHAZADO";
}

/**
 * ¿El contratista puede editar las cantidades?
 *
 * Sí cuando el día NO tiene reportes que proteger —el caso normal, sin
 * ajuste— y sí cuando el PL2 ya autorizó. Con la solicitud apenas enviada NO:
 * editar antes del visto bueno vaciaría de sentido el permiso.
 */
export function puedeEditarRecursos(
  ajuste: AjusteRr | undefined,
  requiereAjuste: boolean,
): boolean {
  if (!requiereAjuste) return true;
  return ajuste?.estado === "AUTORIZADO" || ajuste?.estado === "EN_REVISION";
}

/** ¿Se puede pedir un ajuste ahora? */
export function puedeSolicitarAjuste(
  ajuste: AjusteRr | undefined,
  requiereAjuste: boolean,
): boolean {
  if (!requiereAjuste) return false;
  return !ajusteEnCurso(ajuste);
}

/** El PL2 decide sobre la solicitud o sobre el cambio ya guardado. */
export function pl2DebeDecidir(ajuste: AjusteRr | undefined): boolean {
  return ajuste?.estado === "SOLICITADO" || ajuste?.estado === "EN_REVISION";
}

/** El INT puede conceptuar mientras el cambio espera decisión del PL2. */
export function intPuedeConceptuar(ajuste: AjusteRr | undefined): boolean {
  return ajuste?.estado === "EN_REVISION";
}

/** Texto de estado para la banda del módulo. */
export function descripcionAjusteRr(ajuste: AjusteRr | undefined): string {
  if (!ajuste) return "";
  switch (ajuste.estado) {
    case "SOLICITADO":
      return `Ajuste solicitado por ${ajuste.solicitadoPor}: ${ajuste.motivo}. Espera autorización del PL2.`;
    case "AUTORIZADO":
      return `Ajuste autorizado por ${ajuste.autorizadoPor ?? "—"}. El contratista puede corregir y guardar.`;
    case "EN_REVISION":
      return ajuste.conceptoInt
        ? `Cambio guardado. Concepto del interventor: "${ajuste.conceptoInt}". Espera aprobación del PL2.`
        : "Cambio guardado. Espera concepto del interventor (opcional) y aprobación del PL2.";
    case "APROBADO":
      return `Ajuste aprobado por ${ajuste.aprobadoPor ?? "—"} el ${(ajuste.aprobadoEn ?? "").slice(0, 10)}. Los reportes quedaron actualizados.`;
    case "RECHAZADO":
      return `Ajuste rechazado por ${ajuste.rechazadoPor ?? "—"}${ajuste.observacionesPl2 ? `: ${ajuste.observacionesPl2}` : ""}.`;
  }
}
